import { ApolloClient, ApolloLink, HttpLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { notifyError } from "common/utils/app-monitoring/notify";
import { RetryLink } from "@apollo/client/link/retry";
import { hasStatusCode } from "../lib/errorGuards";
import { createCache } from "./createCache";
import { csrfHeaderLink, csrfRetryLink } from "./csrfLink";

// 4xx errors (403, 401, 422, etc.) indicate expected auth/session failures and
// should not be reported as application errors. Filtered by either `statusCode`
// (when present) or Apollo error message pattern (when `statusCode` is stripped
// by error propagation). Only 5xx or network-level errors indicate actual bugs
// worth reporting. 2xx/3xx status codes on parse errors (e.g. CDN returning
// HTML with 200) should still be reported.
const CLIENT_ERROR_MESSAGE = /^Response not successful: Received status code 4\d\d$/;

export const isServerOrNetworkError = (error: unknown): boolean => {
  if (hasStatusCode(error)) {
    return error.statusCode >= 500 || error.statusCode < 400;
  }
  // When statusCode is stripped by Apollo's error propagation, fall back to
  // message-based detection to avoid reporting 4xx errors as network errors.
  const msg = (error as { message?: string })?.message;
  if (typeof msg === "string" && CLIENT_ERROR_MESSAGE.test(msg)) {
    return false;
  }
  // No statusCode means a network-level failure (DNS, timeout, etc.)
  return true;
};

// Reports errors to Honeybadger ONLY after all retries (RetryLink + CSRF refresh)
// are exhausted. Positioned first in the chain so it sees final errors only.
const errorReportingLink = new ErrorLink(({ error, operation }) => {
  if (isServerOrNetworkError(error) && typeof window !== "undefined") {
    const err = error as { message?: string };
    const message = typeof err?.message === "string" ? err.message : "Unknown error";
    notifyError(`[GraphQL] ${message}`, {
      context: { operationName: operation.operationName }
    });
  }
});

export const graphqlClient = new ApolloClient({
  cache: createCache(),
  queryDeduplication: true,
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-first"
    },
    query: {
      errorPolicy: "all",
      fetchPolicy: "cache-first"
    },
    mutate: {
      errorPolicy: "all"
    }
  },
  link: ApolloLink.from([
    errorReportingLink,
    csrfRetryLink,
    new RetryLink({
      attempts: {
        max: 3,
        // Under errorPolicy:"all", GraphQL-layer errors resolve normally
        // ({data, errors}) and never reach retryIf. Only transport-level
        // failures (networkError) trigger the Observable error path.
        retryIf: error => !!("networkError" in error && error.networkError)
      }
    }),
    csrfHeaderLink,
    new HttpLink({
      uri: "/graphql",
      credentials: "include"
    })
  ])
});
